import posthog from "posthog-js";
import { axiosBasic } from "../api/axios";
import { config } from "../api/endPoints";

/**
 * Coerce API numeric enums (JSON may return numbers as strings, e.g. "0", "2").
 * @param {*} value
 * @param {number} fallback
 * @returns {number}
 */
export const normalizeSubscriptionNumber = (value, fallback = 0) => {
    if (value === null || value === undefined || value === "") return fallback;
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
};

/**
 * Check if subscription is restricted (Suspended, Cancelled, or Expired)
 * Cancelled-but-still-billing-active is never restricted.
 * Subscription entity status / payment state take precedence over top-level isActive when the API is inconsistent
 * (e.g. isActive true while subscription.status is Suspended and paymentStatus Failed).
 * @param {Object} subscriptionData - Subscription data from API
 * @returns {boolean} - True if subscription is restricted
 */
export const isSubscriptionSuspended = (subscriptionData) => {
    if (!subscriptionData) return false;

    const subscription = subscriptionData?.subscription;
    const trialDaysRemaining = normalizeSubscriptionNumber(subscriptionData?.trialDaysRemaining, 0);
    const subscriptionStatus = normalizeSubscriptionNumber(subscription?.status, -1);
    const isCancelledButBillingActive = subscriptionData?.isCancelledButBillingActive ?? false;
    const isActive = subscriptionData?.isActive ?? false;

    //For subscription users who have cancelled but billing is still active, they should have access until the billing period ends
    if (isCancelledButBillingActive) {
        return false;
    }

    const restrictedByStatus =
        (subscriptionStatus === 0 && trialDaysRemaining === 0) ||
        subscriptionStatus === 2 ||
        (subscriptionStatus === 3 && !isCancelledButBillingActive) ||
        subscriptionStatus === 4;

    if (restrictedByStatus) {
        return true;
    }

    if (isActive) {
        return false;
    }

    return false;
};

/**
 * Get subscription status name
 * @param {number} status - Subscription status code
 * @returns {string} - Status name
 */
export const getSubscriptionStatusName = (status) => {
    const statusMap = {
        0: 'Trial',
        1: 'Active',
        2: 'Suspended',
        3: 'Cancelled',
        4: 'Expired'
    };
    return statusMap[status] || 'Unknown';
};

/** In-flight GET and short TTL cache to avoid duplicate Payment/Subscription calls when many screens mount. */
let subscriptionFetchInFlight = null;
let subscriptionCache = { data: null, expiresAt: 0 };
const SUBSCRIPTION_CACHE_MS = 45000;

/** Clear cache after billing mutations so next fetch is fresh. */
export const invalidateSubscriptionCache = () => {
    subscriptionCache = { data: null, expiresAt: 0 };
};

/** Fired after Billing renew/cancel/checkout refresh so hooks can refetch (they normally only run on mount). */
export const HRM_SUBSCRIPTION_CHANGED_EVENT = "hrm-subscription-changed";

export const dispatchSubscriptionChanged = (detail = {}) => {
    if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(HRM_SUBSCRIPTION_CHANGED_EVENT, { detail }));
    }
};

/**
 * Fetch subscription data (deduped + optional cache).
 * @param {{ force?: boolean }} [options] - force: true skips cache and does not share in-flight request
 * @returns {Promise<Object|null>} - Subscription data or null
 */
export const fetchSubscriptionData = async (options = {}) => {
    const force = options.force === true;

    if (!force && subscriptionCache.data != null && Date.now() < subscriptionCache.expiresAt) {
        return subscriptionCache.data;
    }
    if (!force && subscriptionFetchInFlight) {
        return subscriptionFetchInFlight;
    }

    const requestPromise = (async () => {
        try {
            const { REST_API } = config;
            const response = await axiosBasic.get(REST_API.Payment.Subscription);

            let data = null;
            if (response.data && response.data.success) {
                data = response.data.data;
            }
            if (data) {
                subscriptionCache = { data, expiresAt: Date.now() + SUBSCRIPTION_CACHE_MS };
            }
            return data;
        } catch (err) {
            if (typeof posthog !== "undefined" && posthog.capture) {
                posthog.capture("fetchSubscriptionData", {
                    type: "exception",
                    success: false,
                    reason: err?.response?.data?.message ?? err?.message ?? "Request failed",
                    status: err?.response?.status,
                });
            }
            return null;
        } finally {
            if (subscriptionFetchInFlight === requestPromise) {
                subscriptionFetchInFlight = null;
            }
        }
    })();

    if (!force) {
        subscriptionFetchInFlight = requestPromise;
    }
    return requestPromise;
};

/**
 * Get subscription status message
 * @param {Object} subscriptionData - Subscription data from API
 * @returns {string|null} - Status message or null
 */
export const getSubscriptionStatusMessage = (subscriptionData) => {
    if (!subscriptionData) return null;

    const subscription = subscriptionData?.subscription;
    const trialDaysRemaining = normalizeSubscriptionNumber(subscriptionData?.trialDaysRemaining, 0);
    const subscriptionStatus = normalizeSubscriptionNumber(subscription?.status, -1);
    const isCancelledButBillingActive = subscriptionData?.isCancelledButBillingActive ?? false;
    const daysRemainingInBillingPeriod = normalizeSubscriptionNumber(
        subscriptionData?.daysRemainingInBillingPeriod,
        0
    );
    const billingPeriodEnd = subscriptionData?.billingPeriodEnd;

    // Handle cancelled-but-active scenario
    if (isCancelledButBillingActive) {
        const endDate = billingPeriodEnd
            ? new Date(billingPeriodEnd).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric'
            })
            : 'the end of your billing period';

        if (daysRemainingInBillingPeriod > 0) {
            return `Your subscription has been cancelled, but you'll continue to have access until ${endDate} (${daysRemainingInBillingPeriod} ${daysRemainingInBillingPeriod === 1 ? 'day' : 'days'} remaining).`;
        } else {
            return `Your subscription has been cancelled. Access will end on ${endDate}.`;
        }
    }

    // Handle different subscription statuses
    if (subscriptionStatus === 0 && trialDaysRemaining === 0) {
        return "Your trial has ended. Please add a payment method to continue using the service.";
    }

    if (subscriptionStatus === 2) {
        return "Your subscription has been suspended. Please update your payment information to continue using the service.";
    }

    if (subscriptionStatus === 3 && !isCancelledButBillingActive) {
        return "Your subscription has been cancelled. Please update your payment information to reactivate your subscription.";
    }

    if (subscriptionStatus === 4) {
        return "Your subscription has expired. Please update your payment information to renew your subscription.";
    }

    return null;
};

/**
 * Get subscription restriction error message for non-admin users
 * @param {Object} subscriptionData - Subscription data from API
 * @returns {string|null} - Error message or null
 */
export const getSubscriptionRestrictionMessage = (subscriptionData) => {
    if (!subscriptionData) return null;

    const subscription = subscriptionData?.subscription;
    const trialDaysRemaining = normalizeSubscriptionNumber(subscriptionData?.trialDaysRemaining, 0);
    const subscriptionStatus = normalizeSubscriptionNumber(subscription?.status, -1);
    const isCancelledButBillingActive = subscriptionData?.isCancelledButBillingActive ?? false;

    if (isCancelledButBillingActive) {
        return null;
    }

    // Handle different subscription statuses with specific messages for non-admin users
    if (subscriptionStatus === 0 && trialDaysRemaining === 0) {
        return "Access is currently unavailable. Your company's trial has ended. Please contact your administrator to add a payment method.";
    }

    if (subscriptionStatus === 2) {
        return "Access is temporarily unavailable. Your company's subscription is currently suspended. Please check back later or contact support if you need assistance.";
    }

    if (subscriptionStatus === 3 && !isCancelledButBillingActive) {
        return "Access is currently unavailable. Your company's subscription has been cancelled. Please contact support for more information.";
    }

    if (subscriptionStatus === 4) {
        return "Access is currently unavailable. Your company's subscription has expired. Please contact support to renew your subscription.";
    }

    // Check if subscription is restricted (any of the above conditions)
    if (isSubscriptionSuspended(subscriptionData)) {
        return "Access is currently unavailable. Your company's subscription is not active. Please contact support for assistance.";
    }

    return null;
};

